import type { ReadonlyURLSearchParams } from "next/navigation";

import type { UseServiceMessageData } from "#app/_ui/components/errors/service-messages";
import type { UseQueryPageOutageData } from "#app/_ui/components/outages/use-query-page-outage";

export function reduceSignInAlerts({
  searchParams,
  statusCode,
  statusMessage,
  dataPageMessage,
  dataServiceMessage,
}: {
  searchParams: ReadonlyURLSearchParams;
  statusCode: number;
  statusMessage: string | null;
  dataPageMessage: UseQueryPageOutageData;
  dataServiceMessage?: UseServiceMessageData;
}) {
  // Session statuses
  const isSessionExpired =
    statusCode === 0 && searchParams.get("sessionExpired") === "true";
  const isSessionSignOut =
    statusCode === 0 && searchParams.get("sessionSignOut") === "true";
  const isRequestBlockedAuthed =
    statusCode === 0 && searchParams.get("requestBlocked") === "A";
  const isRequestBlockedUnauthed =
    statusCode === 0 && searchParams.get("requestBlocked") === "U";

  // Async mutation statuses
  const isMutationError = statusCode >= 400;
  const hasMutationMessage =
    typeof statusMessage === "string" && statusMessage.length > 0;

  // Determine which alert type to show
  let alertSecondaryCode: number = statusCode;
  let alertSecondaryMessage: string | null = null;
  if (isMutationError) {
    alertSecondaryCode = statusCode;
    alertSecondaryMessage = hasMutationMessage ? statusMessage : null;
  } else if (isRequestBlockedAuthed) {
    alertSecondaryCode = 5;
    alertSecondaryMessage = null;
  } else if (isRequestBlockedUnauthed) {
    alertSecondaryCode = 6;
    alertSecondaryMessage = null;
  } else if (isSessionExpired) {
    alertSecondaryCode = 2;
    alertSecondaryMessage = null;
  } else if (isSessionSignOut) {
    alertSecondaryCode = 1;
    alertSecondaryMessage = null;
  }

  // Async service-outage statuses
  const isServiceDisabled = dataServiceMessage?.isAvailable === false;
  const hasServiceDisabledMessage =
    typeof dataServiceMessage?.statusMessage === "string" &&
    dataServiceMessage.statusMessage.length > 0;
  const isServiceEnabledWithMessage =
    !isServiceDisabled && hasServiceDisabledMessage;

  // Async route-disabled statuses
  const isRouteDisabled = dataPageMessage?.isAvailable === false;
  const hasRouteDisableMessage =
    typeof dataPageMessage?.statusMessage === "string" &&
    dataPageMessage.statusMessage.length > 0;
  const isRouteEnabledWithMessage = !isRouteDisabled && hasRouteDisableMessage;

  // Combined async statuses
  const isPageUnavailable = isServiceDisabled || isRouteDisabled;

  // Determine which alert type to show
  let alertPrimaryCode: number = 0;
  let alertPrimaryMessage: string | null = null;
  if (isPageUnavailable) {
    alertPrimaryCode = 4;
    alertPrimaryMessage =
      (dataServiceMessage?.statusMessage || dataPageMessage?.statusMessage) ??
      null;
  } else if (isServiceEnabledWithMessage) {
    alertPrimaryCode = 3;
    alertPrimaryMessage = dataServiceMessage.statusMessage;
  } else if (isRouteEnabledWithMessage) {
    alertPrimaryCode = 3;
    alertPrimaryMessage = dataPageMessage.statusMessage;
  }

  return {
    alertPrimaryCode,
    alertPrimaryMessage,
    alertSecondaryCode,
    alertSecondaryMessage,
    isPageUnavailable,
  };
}
